import { makeAutoObservable, runInAction } from 'mobx'
import type { AxiosError } from 'axios'
import agent from '../api/agent'
import type { MixedFeedItemDto, MixedFeedResultDto } from '../models/mixedFeed'

export type FeedItemType = 'Shoot' | 'Post' | 'Blog';

export interface FeedItem {
  id: string;
  type: FeedItemType;
  userId: string;
  createdAt: string;
  expiresAt?: string | null;
  title?: string;
  description?: string;
  excerpt?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  tags?: string[];
  firearmName?: string;
  cartridgeLabel?: string;
  userDisplayName?: string;
  userAvatarUrl?: string;
  /** Native shoot editor overlays (JSON); Slop “Shoot” items omit this. */
  textBoxesJson?: string;
  /** API shoot media type: photo | video */
  mediaType?: string;
  likeCount: number;
  commentCount: number;
  viewCount: number;
}

function normalizeType (raw: string | undefined | null): FeedItemType {
  const t = (raw || '').trim()
  if (t === 'Post') return 'Post'
  if (t === 'Blog') return 'Blog'
  return 'Shoot'
}

function parseMixedFeedResult (data: MixedFeedResultDto | Record<string, unknown>): {
  items: MixedFeedItemDto[];
  nextCursor: string | null;
  hasMore: boolean;
} {
  const d = data as Record<string, unknown>
  const rawItems = (d.items ?? d.Items ?? []) as MixedFeedItemDto[]
  const nextCursor = (d.nextCursor ?? d.NextCursor ?? null) as string | null
  const hasMore = Boolean(d.hasMore ?? d.HasMore ?? false)
  return { items: Array.isArray(rawItems) ? rawItems : [], nextCursor, hasMore }
}

function mapDtoToFeedItem (dto: MixedFeedItemDto): FeedItem {
  return {
    id: String(dto.id),
    type: normalizeType(dto.type),
    userId: dto.userId,
    createdAt: dto.createdAt,
    expiresAt: dto.expiresAt ?? undefined,
    title: dto.title ?? undefined,
    description: dto.description ?? undefined,
    excerpt: dto.excerpt ?? undefined,
    mediaUrl: dto.mediaUrl ?? undefined,
    thumbnailUrl: dto.thumbnailUrl ?? undefined,
    tags: dto.tags ?? undefined,
    firearmName: dto.firearmName ?? undefined,
    cartridgeLabel: dto.cartridgeLabel ?? undefined,
    userDisplayName: dto.userDisplayName ?? undefined,
    userAvatarUrl: dto.userAvatarUrl ?? undefined,
    textBoxesJson: dto.textBoxesJson ?? undefined,
    mediaType: dto.mediaType ?? undefined,
    likeCount: dto.likeCount ?? 0,
    commentCount: dto.commentCount ?? 0,
    viewCount: dto.viewCount ?? 0
  }
}

class SlopFeedStore {
  feedItems: FeedItem[] = []
  nextCursor: string | null = null
  hasMore = true
  isLoading = false
  error: string | null = null
  /** True after the first page load completes successfully (used for empty state). */
  initialLoadSucceeded = false

  readonly defaultLimit = 10

  constructor () {
    makeAutoObservable(this)
  }

  /**
   * Fetch a page. `cursor === null` replaces the list; non-null appends (infinite scroll).
   */
  fetchFeedItems = async (limit: number = this.defaultLimit, cursor: string | null = null) => {
    if (this.isLoading) return

    if (cursor !== null) {
      if (!this.hasMore) return
      if (!this.nextCursor) return
    }

    this.isLoading = true
    if (cursor === null) {
      this.error = null
    }

    try {
      const effectiveCursor = cursor === null ? null : this.nextCursor
      const raw = await agent.SlopFeed.getMixedFeed(limit, effectiveCursor)
      const parsed = parseMixedFeedResult(raw as MixedFeedResultDto)
      const newItems = parsed.items.map(mapDtoToFeedItem)

      runInAction(() => {
        if (cursor === null) {
          this.feedItems = newItems
          this.initialLoadSucceeded = true
        } else {
          const existing = new Set(this.feedItems.map((i) => i.id))
          const merged = [...this.feedItems]
          for (const item of newItems) {
            if (!existing.has(item.id)) {
              existing.add(item.id)
              merged.push(item)
            }
          }
          this.feedItems = merged
        }
        this.nextCursor = parsed.nextCursor
        this.hasMore = parsed.hasMore
        this.isLoading = false
      })
    } catch (err: unknown) {
      let message = 'Failed to load feed'
      const ax = err as AxiosError<{ detail?: string }>
      if (ax?.response?.data?.detail) message = String(ax.response.data.detail)
      else if (ax?.message) message = ax.message
      else if (err instanceof Error) message = err.message
      runInAction(() => {
        this.error = message
        this.isLoading = false
        if (cursor === null) {
          this.feedItems = []
          this.initialLoadSucceeded = false
          this.hasMore = true
          this.nextCursor = null
        }
      })
    }
  }

  retry = async () => {
    if (this.isLoading) return
    if (this.feedItems.length > 0 && this.nextCursor) {
      await this.fetchFeedItems(this.defaultLimit, this.nextCursor)
    } else {
      await this.fetchFeedItems(this.defaultLimit, null)
    }
  }

  clearError = () => {
    runInAction(() => {
      this.error = null
    })
  }

  resetFeed = () => {
    runInAction(() => {
      this.feedItems = []
      this.nextCursor = null
      this.hasMore = true
      this.isLoading = false
      this.error = null
      this.initialLoadSucceeded = false
    })
  }

  removeFeedItemById = (id: string) => {
    runInAction(() => {
      this.feedItems = this.feedItems.filter((i) => i.id !== id)
    })
  }
}

export default SlopFeedStore
